import { useId, useRef } from "react";
import { Info, Maximize2, TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { cn } from "@/lib/utils";

interface FullscreenBlockingOverlayProps {
  /** Click handler for the CTA. Must be called from inside this synchronous
   *  event so requestFullscreen() banks the user-activation token. */
  onEnter: () => void;
  /** Switches the copy between first-entry ("Enter fullscreen mode to begin")
   *  and re-entry ("Return to fullscreen mode"). Owned by useFullscreen — a
   *  one-way flip that goes true on the first successful fullscreenchange. */
  hasEverEntered: boolean;
  /** `useFullscreen().exitCause`. `"file_picker"` = the OS file dialog of a
   *  file-upload question took the browser out of fullscreen (EF-11); that
   *  exit is not recorded, so the card informs instead of warning. */
  cause?: "file_picker" | null;
}

/**
 * Full-viewport blocking overlay that swallows all input and pins the test
 * until the candidate clicks the CTA to (re-)enter fullscreen. Replaces the
 * earlier non-blocking FullscreenWarningCard — the soft-warn UX let candidates
 * spend most of the test out of fullscreen without realising, and the camera
 * permission prompt routinely released the just-banked fullscreen state on
 * the form-submit click.
 *
 * Render contract: mounted by Question.tsx whenever
 *   `useFullscreen().isSupported && !useFullscreen().isFullscreen`.
 * Browsers that don't support the Fullscreen API skip this entirely (we
 * still log `fullscreen_unavailable: not_supported` once on Q1 mount).
 *
 * role="alertdialog" + aria-modal so screen readers announce it correctly.
 *
 * Focus is TRAPPED here via useFocusTrap, not merely placed. The backdrop
 * (z-50, pointer-events-auto) only ever blocked the POINTER: the question
 * underneath stays in the tab order, so before 2026-08-13 a candidate could
 * leave fullscreen, Tab straight past this overlay into the question, and
 * answer + advance with Space/Enter while the card read "The test is paused".
 * The exit is still detected and flips integrity to flagged, so such an attempt
 * arrives visibly suspect — but the gate is documented as having no escape
 * hatch, and it now actually has none (qa-1, 2026-08-13 pre-ship audit).
 *
 * The file-picker exit (`cause="file_picker"`) changes the TONE only — info
 * accent, no warning icon, "not counted against you" — never the gate: same
 * backdrop, same trap, same single button, because the browser hands
 * fullscreen back on a real click and nothing else. The copy is a placeholder
 * until Emilia words it (owed.md).
 */
export function FullscreenBlockingOverlay({
  onEnter,
  hasEverEntered,
  cause = null,
}: FullscreenBlockingOverlayProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const descId = useId();

  // Lands focus on the CTA (the only focusable in here) AND keeps it there:
  // Tab / Shift+Tab cannot reach the paused question behind the backdrop.
  // Browsers only grant fullscreen from a real user gesture, so the immediate
  // path stays press-Enter-to-continue.
  useFocusTrap(dialogRef, true);

  // Only an EXIT can be the picker's: on a first entry there is nothing that
  // "closed", so the entry copy wins whatever the cause says.
  const fromFilePicker = hasEverEntered && cause === "file_picker";

  const title = !hasEverEntered
    ? "Fullscreen mode required"
    : fromFilePicker
      ? "Fullscreen closed while you chose a file"
      : "You exited fullscreen";
  const body = !hasEverEntered
    ? "This assessment runs in fullscreen mode. Click the button below to enter fullscreen and begin."
    : fromFilePicker
      ? "That's expected and is not counted against you. Return to fullscreen to carry on — your time is still counting down."
      : "The test is paused until you return. Your time is still counting down.";
  const cta = hasEverEntered
    ? "Return to fullscreen mode"
    : "Enter fullscreen mode";

  return (
    // Full-viewport backdrop: covers EVERYTHING (header, question body, camera
    // preview at z-40). pointer-events-auto swallows clicks; no escape hatch
    // from the test underneath until requestFullscreen succeeds.
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 px-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div
        className={cn(
          "max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-lg border border-border border-l-4 bg-card p-6 shadow-xl",
          fromFilePicker ? "border-l-info" : "border-l-destructive",
        )}
      >
        <div className="flex items-start gap-3">
          {fromFilePicker ? (
            <Info
              className="mt-0.5 h-5 w-5 shrink-0 text-info"
              aria-hidden="true"
            />
          ) : (
            <TriangleAlert
              className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
              aria-hidden="true"
            />
          )}
          <div className="min-w-0 flex-1 space-y-4">
            <div className="space-y-1">
              <p
                id={titleId}
                className="text-base font-semibold text-foreground"
              >
                {title}
              </p>
              <p id={descId} className="text-sm text-muted-foreground">
                {body}
              </p>
            </div>
            <Button
              type="button"
              onClick={onEnter}
              className="cursor-pointer"
            >
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
              {cta}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
